/**
 * Collapsible "New dealer" form. Mirrors the open/close UX of
 * RequestApprovalForm: a single button that expands into a panel.
 *
 * Self-gates on the `dealer.write` permission — renders nothing if the
 * current user lacks it, so list pages don't need to know the rule. The
 * backend PermissionGuard is the real enforcement; this is UX only.
 *
 * On success the dealers list query is invalidated so the new row shows
 * up without a manual refresh.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CreateDealerRequest, Dealer, DealerRiskTier } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

const RISK_TIERS: readonly DealerRiskTier[] = ["A", "B", "C"];

export function CreateDealerForm(): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("dealer.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [stockLimit, setStockLimit] = useState("0");
  const [riskTier, setRiskTier] = useState<DealerRiskTier>("B");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateDealerRequest) => api.post<Dealer>("/api/dealers", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["dealers", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setName("");
    setStockLimit("0");
    setRiskTier("B");
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (name.trim().length < 1) {
      setError("Name is required.");
      return;
    }
    const stock = Number(stockLimit);
    if (!Number.isInteger(stock) || stock < 0) {
      setError("Stock limit must be a non-negative whole number.");
      return;
    }
    mutation.mutate({ name: name.trim(), stockLimit: stock, riskTier });
  }

  if (!canWrite) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New dealer…</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New dealer</p>

      <Field label="Name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Stock limit" hint="whole number">
          <TextInput
            inputMode="numeric"
            value={stockLimit}
            onChange={(e) => setStockLimit(e.target.value)}
          />
        </Field>
        <Field label="Risk tier">
          <Select value={riskTier} onChange={(e) => setRiskTier(e.target.value as DealerRiskTier)}>
            {RISK_TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending || name.trim().length < 1}>
          {mutation.isPending ? "Creating…" : "Create dealer"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
